'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Smartphone,
  Monitor,
  Shield,
  Brain,
  FileText,
  CheckCircle,
  Sparkles,
} from 'lucide-react';
import styles from './AuditProgressScreen.module.css';

// ============================================
// Types
// ============================================

export interface AuditProgressStep {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  estimatedDuration: number; // in seconds
  phase: 'capture' | 'analysis' | 'report';
}

export interface AuditProgressProps {
  /** Current step being processed (from API) */
  currentStepId?: string;
  /** Whether the audit is complete */
  isComplete?: boolean;
  /** Audit tier for timing estimates */
  tier?: 'free' | 'paid';
  /** URL being audited */
  url?: string;
  /** Error message if audit failed */
  error?: string;
  /** Callback when user wants to cancel */
  onCancel?: () => void;
}

// ============================================
// Constants
// ============================================

const STEPS_FREE: AuditProgressStep[] = [
  {
    id: 'desktop-capture',
    label: 'Capturing Desktop View',
    description: 'Taking a full-page screenshot at 1920×1080',
    icon: Monitor,
    estimatedDuration: 12,
    phase: 'capture',
  },
  {
    id: 'mobile-capture',
    label: 'Capturing Mobile View',
    description: 'Emulating iPhone 14 Pro viewport',
    icon: Smartphone,
    estimatedDuration: 12,
    phase: 'capture',
  },
  {
    id: 'accessibility',
    label: 'Accessibility Audit',
    description: 'Checking WCAG 2.1 compliance',
    icon: Shield,
    estimatedDuration: 6,
    phase: 'analysis',
  },
  {
    id: 'heuristic',
    label: 'UX Heuristic Analysis',
    description: 'Evaluating against 167 research-backed Audit Criteria',
    icon: Brain,
    estimatedDuration: 8,
    phase: 'analysis',
  },
  {
    id: 'report',
    label: 'Generating Report',
    description: 'Compiling findings and recommendations',
    icon: FileText,
    estimatedDuration: 2,
    phase: 'report',
  },
];

const STEPS_PAID: AuditProgressStep[] = [
  {
    id: 'desktop-capture',
    label: 'Capturing Desktop View',
    description: 'Full-page screenshot with extended scroll depth',
    icon: Monitor,
    estimatedDuration: 18,
    phase: 'capture',
  },
  {
    id: 'mobile-capture',
    label: 'Capturing Mobile View',
    description: 'Mobile-optimized capture with touch emulation',
    icon: Smartphone,
    estimatedDuration: 18,
    phase: 'capture',
  },
  {
    id: 'accessibility',
    label: 'Accessibility Audit',
    description: 'Deep WCAG 2.1 AA/AAA compliance check',
    icon: Shield,
    estimatedDuration: 8,
    phase: 'analysis',
  },
  {
    id: 'pro-analysis',
    label: 'UX Pattern Analysis',
    description: 'Expert analysis of visual design and UX patterns',
    icon: Sparkles,
    estimatedDuration: 25,
    phase: 'analysis',
  },
  {
    id: 'heuristic',
    label: 'UX Heuristic Analysis',
    description: 'Matching against 167 Audit Criteria',
    icon: Brain,
    estimatedDuration: 10,
    phase: 'analysis',
  },
  {
    id: 'report',
    label: 'Generating Full Report',
    description: 'Creating comprehensive analysis with PDF export',
    icon: FileText,
    estimatedDuration: 5,
    phase: 'report',
  },
];

const FUN_FACTS = [
  { text: 'Users form opinions about a website in just 0.05 seconds', source: 'Lindgaard et al., 2006' },
  { text: '88% of users are less likely to return after a bad UX experience', source: 'Toptal Research' },
  { text: 'Every $1 invested in UX returns $100 on average', source: 'Forrester Research' },
  { text: 'Mobile users are 5x more likely to abandon a task with poor UX', source: 'Google Research' },
  { text: 'Consistent design can increase revenue by up to 33%', source: 'McKinsey & Company' },
  { text: '94% of first impressions are design-related', source: 'ResearchGate Study' },
  { text: 'Users read only 20% of text on an average web page', source: 'Nielsen Norman Group' },
  { text: 'Color improves brand recognition by up to 80%', source: 'University of Loyola' },
  { text: 'F-shaped reading pattern dominates most web browsing', source: 'Nielsen Norman Group' },
  { text: 'Page load delay of 1s can reduce conversions by 7%', source: 'Akamai Research' },
];

const TIPS = [
  'Prioritize fixing critical issues first—they have the highest impact on user experience.',
  'Focus on mobile issues—over 60% of web traffic now comes from mobile devices.',
  'Accessibility improvements benefit all users, not just those with disabilities.',
  'Small changes to button colors and placement can increase conversions by 20%+.',
  'Clear error messages can reduce user frustration and support tickets significantly.',
];

// ============================================
// Component
// ============================================

export function AuditProgressScreen({
  currentStepId: _currentStepId, // Reserved for future real-time API step sync
  isComplete = false,
  tier = 'free',
  url,
  error,
  onCancel,
}: AuditProgressProps) {
  const steps = tier === 'paid' ? STEPS_PAID : STEPS_FREE;
  const totalEstimatedTime = steps.reduce((sum, step) => sum + step.estimatedDuration, 0);

  // State
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [currentFact, setCurrentFact] = useState(() =>
    FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)]
  );
  const [currentTip, setCurrentTip] = useState(() =>
    TIPS[Math.floor(Math.random() * TIPS.length)]
  );

  // Calculate progress percentage
  const progress = useMemo(() => {
    if (isComplete) return 100;

    // Calculate based on completed steps + partial progress of current step
    let completedTime = 0;
    for (let i = 0; i < activeStepIndex; i++) {
      completedTime += steps[i].estimatedDuration;
    }

    // Add partial progress of current step based on elapsed time
    const currentStep = steps[activeStepIndex];
    if (currentStep) {
      const stepElapsed = Math.min(
        elapsedTime - completedTime,
        currentStep.estimatedDuration
      );
      completedTime += Math.max(0, stepElapsed);
    }

    return Math.min(95, Math.round((completedTime / totalEstimatedTime) * 100));
  }, [elapsedTime, activeStepIndex, steps, totalEstimatedTime, isComplete]);

  // Simulate step progression based on elapsed time
  useEffect(() => {
    if (isComplete || error) return;

    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isComplete, error]);

  // Progress through steps based on elapsed time
  useEffect(() => {
    if (isComplete) {
      setCompletedSteps(new Set(steps.map((s) => s.id)));
      setActiveStepIndex(steps.length);
      return;
    }

    let accumulatedTime = 0;
    for (let i = 0; i < steps.length; i++) {
      accumulatedTime += steps[i].estimatedDuration;
      if (elapsedTime < accumulatedTime) {
        setActiveStepIndex(i);
        // Mark previous steps as completed
        const completed = new Set(steps.slice(0, i).map((s) => s.id));
        setCompletedSteps(completed);
        return;
      }
    }

    // All steps should be "complete" but we're still waiting for API
    setActiveStepIndex(steps.length - 1);
    setCompletedSteps(new Set(steps.slice(0, -1).map((s) => s.id)));
  }, [elapsedTime, steps, isComplete]);

  // Rotate fun facts every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFact(FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)]);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  // Rotate tips every 12 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip(TIPS[Math.floor(Math.random() * TIPS.length)]);
    }, 12000);

    return () => clearInterval(interval);
  }, []);

  // Format time remaining
  const formatTimeRemaining = useCallback(() => {
    const remaining = Math.max(0, totalEstimatedTime - elapsedTime);
    if (remaining === 0) return 'Almost done...';
    if (remaining < 60) return `~${remaining} seconds remaining`;
    const minutes = Math.ceil(remaining / 60);
    return `~${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
  }, [totalEstimatedTime, elapsedTime]);

  // Get current phase label
  const currentPhase = useMemo(() => {
    const step = steps[activeStepIndex];
    if (!step) return 'Finalizing';
    switch (step.phase) {
      case 'capture':
        return 'Screenshot Capture';
      case 'analysis':
        return 'Analysis Phase';
      case 'report':
        return 'Report Generation';
      default:
        return 'Processing';
    }
  }, [activeStepIndex, steps]);

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.errorState}>
            <div className={styles.errorIcon}>!</div>
            <h2 className={styles.errorTitle}>Analysis Failed</h2>
            <p className={styles.errorMessage}>{error}</p>
            {onCancel && (
              <button className={styles.retryButton} onClick={onCancel}>
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        {/* Header */}
        <header className={styles.header}>
          <span className={styles.phaseLabel}>{currentPhase}</span>
          <h1 className={styles.title}>Analyzing Your Website</h1>
          {url && <p className={styles.url}>{url}</p>}
        </header>

        {/* Progress Ring */}
        <div className={styles.progressRing}>
          <svg className={styles.ringSvg} viewBox="0 0 120 120">
            {/* Background ring */}
            <circle
              className={styles.ringBg}
              cx="60"
              cy="60"
              r="54"
              strokeWidth="6"
              fill="none"
            />
            {/* Progress ring */}
            <circle
              className={styles.ringProgress}
              cx="60"
              cy="60"
              r="54"
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              style={{
                strokeDasharray: `${2 * Math.PI * 54}`,
                strokeDashoffset: `${2 * Math.PI * 54 * (1 - progress / 100)}`,
              }}
            />
          </svg>
          <div className={styles.ringContent}>
            <span className={styles.progressNumber}>{progress}</span>
            <span className={styles.progressPercent}>%</span>
          </div>
        </div>

        {/* Time estimate */}
        <p className={styles.timeEstimate}>{formatTimeRemaining()}</p>

        {/* Steps Timeline */}
        <div className={styles.stepsContainer}>
          <div className={styles.stepsLine} />
          {steps.map((step, index) => {
            const isCompleted = completedSteps.has(step.id);
            const isCurrent = index === activeStepIndex && !isComplete;
            const Icon = step.icon;

            return (
              <div
                key={step.id}
                className={`${styles.step} ${
                  isCompleted
                    ? styles.stepCompleted
                    : isCurrent
                      ? styles.stepCurrent
                      : styles.stepPending
                }`}
              >
                <div className={styles.stepIconWrapper}>
                  {isCompleted ? (
                    <CheckCircle size={20} className={styles.stepCheckIcon} />
                  ) : (
                    <Icon size={20} className={styles.stepIcon} />
                  )}
                  {isCurrent && <div className={styles.stepPulse} />}
                </div>
                <div className={styles.stepInfo}>
                  <span className={styles.stepLabel}>{step.label}</span>
                  {isCurrent && (
                    <span className={styles.stepDescription}>{step.description}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Fun Fact */}
        <div className={styles.factCard}>
          <div className={styles.factHeader}>
            <Sparkles size={16} className={styles.factIcon} />
            <span className={styles.factLabel}>Did you know?</span>
          </div>
          <p className={styles.factText}>{currentFact.text}</p>
          <span className={styles.factSource}>— {currentFact.source}</span>
        </div>

        {/* Tip */}
        <div className={styles.tipCard}>
          <span className={styles.tipLabel}>Pro Tip</span>
          <p className={styles.tipText}>{currentTip}</p>
        </div>

        {/* Footer */}
        <footer className={styles.footer}>
          <p className={styles.footerText}>
            Powered by <strong>167 research-backed Audit Criteria</strong>
          </p>
          <p className={styles.footerMeta}>
            {tier === 'paid' ? 'Full UX Report' : 'Basic Report'} • 12 audit categories
          </p>
        </footer>
      </div>
    </div>
  );
}
